/* global BigInt */
const SCALE = 1000000n;

export function creditDecimalToMicrocredits(value) {
  const text = String(value ?? '0');
  if (!/^(0|[1-9][0-9]*)(\.[0-9]{1,6})?$/.test(text)) throw new Error('invalid LedgerLoop credit value');
  const [whole, fraction = ''] = text.split('.');
  return (BigInt(whole) * SCALE + BigInt(fraction.padEnd(6, '0'))).toString();
}

export function microcreditsToCreditDecimal(value) {
  const micros = BigInt(String(value ?? '0'));
  const whole = micros / SCALE;
  const fraction = (micros % SCALE).toString().padStart(6, '0').replace(/0+$/, '');
  return fraction ? `${whole}.${fraction}` : whole.toString();
}

export function formatMicrocredits(value) {
  const decimal = microcreditsToCreditDecimal(value);
  const [whole, fraction] = decimal.split('.');
  const localizedWhole = new Intl.NumberFormat('sv-SE', { maximumFractionDigits: 0 }).format(BigInt(whole));
  return fraction ? `${localizedWhole},${fraction}` : localizedWhole;
}

/**
 * The same value at display precision.
 *
 * `formatMicrocredits` keeps all six decimals, which is right for a ledger and
 * wrong in a sidebar: the balance widget read "98,463058 / 100". Two decimals is
 * what a person needs, with a floor so a small non-zero balance never renders as
 * a flat 0,00 and looks spent.
 */
export function formatMicrocreditsForDisplay(value) {
  const decimal = microcreditsToCreditDecimal(value);
  const asNumber = Number(decimal);
  if (!Number.isFinite(asNumber)) return formatMicrocredits(value);

  if (asNumber !== 0 && Math.abs(asNumber) < 0.01) {
    return asNumber > 0 ? '< 0,01' : '> -0,01';
  }
  return new Intl.NumberFormat('sv-SE', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(asNumber);
}

export function sumMicrocredits(values) {
  return values.reduce((sum, value) => sum + BigInt(String(value ?? '0')), 0n).toString();
}

export function creditPercentage(numeratorMicrocredits, denominatorMicrocredits) {
  const numerator = BigInt(String(numeratorMicrocredits ?? '0'));
  const denominator = BigInt(String(denominatorMicrocredits ?? '0'));
  if (denominator <= 0n) return 0;
  return Number((numerator * 10000n) / denominator) / 100;
}

export function hasMicrocredits(value) {
  return BigInt(String(value ?? '0')) > 0n;
}
